"use client";

import { useEffect, useRef, useState } from "react";
import Turnstile from "react-turnstile";
import { setCookie, getCookie } from "@/utils/cookieUtils";
import { validateEmail, validateRequired, validateMaxLength, validateCaptchaToken, validateCheckbox } from "@/lib/validation-helpers";
import {
  ERROR_TEXT_CLASS,
  FIELD_WRAPPER_CLASS,
  renderRequiredLabel,
  getInputClassesWithError,
} from "@/components/ui/formClasses";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { PrivacyInfoBlock } from "@/components/ui/PrivacyInfoBlock";

type CodigoLiberacaoFormLocalizedStrings = {
  labelNome: string;
  placeholderNome: string;
  labelEmail: string;
  placeholderEmail: string;
  labelEmpresa: string;
  placeholderEmpresa: string;
  labelDepartamento: string;
  placeholderDepartamento: string;
  labelNumSerial: string;
  placeholderNumSerial: string;
  labelComputador: string;
  placeholderComputador: string;
  helperComputador: string;
  labelObservacao: string;
  placeholderObservacao: string;
  labelConsentimento: string;
  submitButton: string;
  submittingButton: string;
  processingButton: string;
  errorNomeRequired: string;
  errorEmailRequired: string;
  errorEmailInvalid: string;
  errorEmpresaRequired: string;
  errorDepartamentoRequired: string;
  errorNumSerialRequired: string;
  errorComputadorRequired: string;
  errorComputadorMaxLength: string;
  errorConsentimentoRequired: string;
  errorCaptchaRequired: string;
  errorCaptchaFailed: string;
  errorConnection: string;
  errorFormSubmission: string;
  captchaWarning: string;
  successSolicitacaoTemplate: string;
  successEmail: string;
  successApprovers: string;
  successAutoApprovedEmail: string;
  successAutoApprovedApprovers: string;
  requiredFieldsLabel: string;
};

type CodigoLiberacaoFormProps = {
  locale: string;
  title: string;
  intro?: string;
  initialNumSerial?: string | undefined;
  initialComputador?: string | undefined;
  localizedStrings: CodigoLiberacaoFormLocalizedStrings;
};

// Form component for codigo de liberacao request with validation and consent checkbox
export function CodigoLiberacaoForm({ locale, title, intro, initialNumSerial, initialComputador, localizedStrings }: CodigoLiberacaoFormProps) {
  const turnstileRef = useRef<string>("");
  const [cookiesLoaded, setCookiesLoaded] = useState(false);

  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    empresa: "",
    departamento: "",
    numSerial: "",
    computador: "",
    observacao: "",
    consentimento: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const [submitMessage, setSubmitMessage] = useState("");

  // Interpolate simple placeholders in localized templates.
  const interpolateMessage = (template: string, values: Record<string, string | number>): string => {
    let result = template;
    for (const [key, value] of Object.entries(values)) {
      result = result.replaceAll(`{${key}}`, String(value));
    }
    return result;
  };

  // Load form data from cookies on mount and when locale changes
  useEffect(() => {
    const savedData = {
      nome: getCookie("form_codigo_liberacao_nome") || "",
      email: getCookie("form_codigo_liberacao_email") || "",
      empresa: getCookie("form_codigo_liberacao_empresa") || "",
      departamento: getCookie("form_codigo_liberacao_departamento") || "",
      numSerial: initialNumSerial || getCookie("form_codigo_liberacao_numSerial") || "",
      computador: initialComputador || getCookie("form_codigo_liberacao_computador") || "",
      observacao: getCookie("form_codigo_liberacao_observacao") || "",
      consentimento: false, // Always start unchecked - not persisted in cookies
    };

    setFormData(savedData);
    setCookiesLoaded(true);
  }, [locale , initialNumSerial , initialComputador]);

  // Save form data to cookies whenever it changes (but not on initial load)
  // NOTE: consentimento checkbox is NOT persisted - must be checked every time
  useEffect(() => {
    if (!cookiesLoaded) return;

    setCookie("form_codigo_liberacao_nome", formData.nome);
    setCookie("form_codigo_liberacao_email", formData.email);
    setCookie("form_codigo_liberacao_empresa", formData.empresa);
    setCookie("form_codigo_liberacao_departamento", formData.departamento);
    setCookie("form_codigo_liberacao_numSerial", formData.numSerial);
    setCookie("form_codigo_liberacao_computador", formData.computador);
    setCookie("form_codigo_liberacao_observacao", formData.observacao);
    // consentimento is intentionally NOT saved to cookies
  }, [formData, cookiesLoaded]);

  // Handle input changes
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, type, value, checked } = e.target as any;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Validate form data
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!validateRequired(formData.nome)) {
      newErrors.nome = localizedStrings.errorNomeRequired;
    }
    if (!validateRequired(formData.email)) {
      newErrors.email = localizedStrings.errorEmailRequired;
    } else if (!validateEmail(formData.email)) {
      newErrors.email = localizedStrings.errorEmailInvalid;
    }
    if (!validateRequired(formData.empresa)) {
      newErrors.empresa = localizedStrings.errorEmpresaRequired;
    }
    if (!validateRequired(formData.departamento)) {
      newErrors.departamento = localizedStrings.errorDepartamentoRequired;
    }
    if (!validateRequired(formData.numSerial)) {
      newErrors.numSerial = localizedStrings.errorNumSerialRequired;
    }
    if (!validateRequired(formData.computador)) {
      newErrors.computador = localizedStrings.errorComputadorRequired;
    } else if (!validateMaxLength(formData.computador, 500)) {
      newErrors.computador = localizedStrings.errorComputadorMaxLength;
    }
    if (!validateCheckbox(formData.consentimento)) {
      newErrors.consentimento = localizedStrings.errorConsentimentoRequired;
    }

    if ( process.env.NEXT_PUBLIC_TURNSTILE_ENABLED === "true" && !validateCaptchaToken(turnstileRef.current)) {
    //if (!validateCaptchaToken(turnstileRef.current)) {
      console.log("Captcha token validation failed:", turnstileRef.current);
      newErrors.turnstile = localizedStrings.errorCaptchaRequired;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if ( process.env.NEXT_PUBLIC_TURNSTILE_ENABLED === "true" ) {
      if (!turnstileRef.current) {
        setErrors((prev) => ({ ...prev, turnstile: localizedStrings.errorCaptchaRequired }));
        return;
      }
    }

    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      const response = await fetch("/api/formularios/codigo-liberacao", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...formData,
          idioma: locale,
          turnstileToken: turnstileRef.current,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setSubmitStatus("error");
        setSubmitMessage(
          data.message || localizedStrings.errorFormSubmission,
        );
        return;
      }

      setSubmitStatus("success");
      
      // Build success messages based on email delivery status
      const idSolic = data.idSolic;
      const isAutoApproved = Boolean(data?.autoApproved);
      const successMessage = isAutoApproved
        ? `${data.message} (ID: ${idSolic})`
        : `${localizedStrings.successSolicitacaoTemplate} ${idSolic}`;
      const emailMessage = isAutoApproved
        ? interpolateMessage(localizedStrings.successAutoApprovedEmail, {
            email: formData.email,
            idSolic,
          })
        : localizedStrings.successEmail;
      const approverMessage = isAutoApproved
        ? "" // para aprovacao automatica, não faz sentido mostrar mensagem sobre email para aprovadores 
        : localizedStrings.successApprovers;
      
      const messages: any = { success: successMessage };
      if (data.emailSentToRequester) {
        messages.email = emailMessage;
      }
      if (data.emailSentToApprovers) {
        messages.approvers = approverMessage;
      }
      
      setSubmitMessage(JSON.stringify(messages));

      // NOTE: Cookies are intentionally NOT cleared on success
      // User can submit another request with same data if needed
      turnstileRef.current = "";
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitStatus("error");
      setSubmitMessage(localizedStrings.errorConnection);
    } finally {
      setIsSubmitting(false);
    }
  };

  const errorClasses = ERROR_TEXT_CLASS;
  const fieldWrapperClasses = FIELD_WRAPPER_CLASS;

  return (
    <section className="rounded-3xl border border-blue-900/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.97),rgba(235,243,252,0.99))] p-6 shadow-[0_18px_45px_rgba(15,23,42,0.08)] sm:p-8">
      <p className="text-sm font-semibold uppercase tracking-[0.22em] text-blue-700">
        Sistech Sistemas
      </p>
      <h1 className="mt-3 text-3xl font-semibold tracking-tight text-blue-950 sm:text-4xl">
        {title}
      </h1>
      {intro ? (
        <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 sm:text-lg">
          {intro}
        </p>
      ) : null}

      {/* Required fields legend */}
      <p className="mt-6 text-sm font-medium text-slate-700">
        <span className="text-red-600">*</span> {localizedStrings.requiredFieldsLabel.replace("* ", "")}
      </p>

      <form className="mt-8 space-y-5" onSubmit={handleSubmit} noValidate>
        {/* Full-width fields */}
        <div className={fieldWrapperClasses}>
          <label htmlFor="nome" className="text-sm font-medium text-blue-950">
            {renderRequiredLabel(localizedStrings.labelNome)}
          </label>
          <input
            id="nome"
            name="nome"
            type="text"
            value={formData.nome}
            onChange={handleChange}
            disabled={isSubmitting}
            className={getInputClassesWithError(!!errors.nome, isSubmitting)}
            placeholder={localizedStrings.placeholderNome}
          />
          {errors.nome && <p className={errorClasses}>{errors.nome}</p>}
        </div>

        <div className={fieldWrapperClasses}>
          <label htmlFor="email" className="text-sm font-medium text-blue-950">
            {renderRequiredLabel(localizedStrings.labelEmail)}
          </label>
          <input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            disabled={isSubmitting}
            className={getInputClassesWithError(!!errors.email, isSubmitting)}
            placeholder={localizedStrings.placeholderEmail}
          />
          {errors.email && <p className={errorClasses}>{errors.email}</p>}
        </div>

        <div className={fieldWrapperClasses}>
          <label htmlFor="empresa" className="text-sm font-medium text-blue-950">
            {renderRequiredLabel(localizedStrings.labelEmpresa)}
          </label>
          <input
            id="empresa"
            name="empresa"
            type="text"
            value={formData.empresa}
            onChange={handleChange}
            disabled={isSubmitting}
            className={getInputClassesWithError(!!errors.empresa, isSubmitting)}
            placeholder={localizedStrings.placeholderEmpresa}
          />
          {errors.empresa && <p className={errorClasses}>{errors.empresa}</p>}
        </div>

        <div className={fieldWrapperClasses}>
          <label htmlFor="departamento" className="text-sm font-medium text-blue-950">
            {renderRequiredLabel(localizedStrings.labelDepartamento)}
          </label>
          <input
            id="departamento"
            name="departamento"
            type="text"
            value={formData.departamento}
            onChange={handleChange}
            disabled={isSubmitting}
            className={getInputClassesWithError(!!errors.departamento, isSubmitting)}
            placeholder={localizedStrings.placeholderDepartamento}
          />
          {errors.departamento && <p className={errorClasses}>{errors.departamento}</p>}
        </div>

        <div className={fieldWrapperClasses}>
          <label htmlFor="numSerial" className="text-sm font-medium text-blue-950">
            {renderRequiredLabel(localizedStrings.labelNumSerial)}
          </label>
          <input
            id="numSerial"
            name="numSerial"
            type="text"
            value={formData.numSerial}
            onChange={handleChange}
            disabled={isSubmitting}
            className={getInputClassesWithError(!!errors.numSerial, isSubmitting)}
            placeholder={localizedStrings.placeholderNumSerial}
          />
          {errors.numSerial && <p className={errorClasses}>{errors.numSerial}</p>}
        </div>

        <div className={fieldWrapperClasses}>
          <label htmlFor="computador" className="text-sm font-medium text-blue-950">
            {renderRequiredLabel(localizedStrings.labelComputador)}
          </label>
          <input
            id="computador"
            name="computador"
            type="text"
            value={formData.computador}
            onChange={handleChange}
            disabled={isSubmitting}
            className={getInputClassesWithError(!!errors.computador, isSubmitting)}
            placeholder={localizedStrings.placeholderComputador}
          />
          <p className="mt-2 text-xs text-slate-600">
            {localizedStrings.helperComputador}
          </p>
          {errors.computador && <p className={errorClasses}>{errors.computador}</p>}
        </div>

        <div className={fieldWrapperClasses}>
          <label htmlFor="observacao" className="text-sm font-medium text-blue-950">
            {localizedStrings.labelObservacao}
          </label>
          <textarea
            id="observacao"
            name="observacao"
            rows={5}
            value={formData.observacao}
            onChange={handleChange}
            disabled={isSubmitting}
            className={getInputClassesWithError(!!errors.observacao, isSubmitting)}
            placeholder={localizedStrings.placeholderObservacao}
          />
        </div>

        {/* Consent Checkbox */}
        <div className={fieldWrapperClasses}>
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              name="consentimento"
              checked={formData.consentimento}
              onChange={handleChange}
              disabled={isSubmitting}
              className={`mt-1 h-5 w-5 rounded border-blue-900/15 text-blue-600 focus:ring-2 focus:ring-blue-300/60 ${
                errors.consentimento ? "border-red-500" : ""
              }`}
            />
            <span className="text-sm text-slate-700">
              {renderRequiredLabel(localizedStrings.labelConsentimento, 'start')}
            </span>
          </label>
          {errors.consentimento && <p className={errorClasses}>{errors.consentimento}</p>}
        </div>

        {/* Cloudflare Turnstile */}
        {process.env.NEXT_PUBLIC_TURNSTILE_ENABLED === "true" ? (
          <div className={fieldWrapperClasses}>
            {process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY &&
            process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY !== "TURNSTILE_SITE_KEY_PLACEHOLDER" ? (
              <div className={`flex justify-center p-4 ${isSubmitting ? "pointer-events-none opacity-50" : ""}`}>
                <Turnstile
                sitekey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY}
                onSuccess={(token) => {
                  turnstileRef.current = token;
                  // Clear error when CAPTCHA is completed
                  if (errors.turnstile) {
                    setErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.turnstile;
                      return newErrors;
                    });
                  }
                }}
                onError={() => {
                  turnstileRef.current = "";
                }}
              />
            </div>
          ) : (
            <div className="rounded-2xl border border-yellow-200 bg-yellow-50 p-4">
              <p className="text-sm text-yellow-800">
                {localizedStrings.captchaWarning}
              </p>
            </div>
          )}
          {errors.turnstile && <p className={errorClasses}>{errors.turnstile}</p>}
        </div>
        )
        :
        (
          <p className="text-sm text-slate-600">
            captcha desativado 
          </p>
        )
        
        }

        {/* Status Messages */}
        {submitStatus === "success" && (
          <div className="rounded-2xl border border-green-200 bg-green-50 p-4 space-y-3">
            {(() => {
              try {
                const messages = JSON.parse(submitMessage);
                return (
                  <>
                    <p className="text-sm text-green-800">{messages.success}</p>
                    {messages.email && (
                      <p className="text-sm text-green-800">{messages.email}</p>
                    )}
                    {messages.approvers && (
                      <p className="text-sm text-green-800">{messages.approvers}</p>
                    )}
                  </>
                );
              } catch {
                return <p className="text-sm text-green-800">{submitMessage}</p>;
              }
            })()}
          </div>
        )}

        {submitStatus === "error" && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
            <p className="text-sm text-red-800">{submitMessage}</p>
          </div>
        )}

        {/* Submit Button */}
        <div className="flex justify-end gap-3 pt-6">
          <button
            type="submit"
            disabled={isSubmitting || submitStatus === "success"}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting && <LoadingSpinner size="sm" ariaLabel="Enviando formulário" />}
            {isSubmitting ? localizedStrings.processingButton : localizedStrings.submitButton}
          </button>
        </div>

        {/* Privacy Information */}
        <PrivacyInfoBlock />
      </form>
    </section>
  );
}
